import { isAppPath } from "./workspaceAddress";
import type { AppPath } from "./workspaceTypes";

export interface ClosedTab {
  readonly route: AppPath;
  readonly name: string;
}

const RECENTLY_CLOSED_LIMIT = 8;

/** The newest closed tab first; no drafts or router history are kept. */
export function rememberClosed(tabs: readonly ClosedTab[], tab: ClosedTab): readonly ClosedTab[] {
  return [tab, ...tabs].slice(0, RECENTLY_CLOSED_LIMIT);
}

/** Taking an entry removes only that occurrence, even when several tabs shared its route. */
export function takeClosed(tabs: readonly ClosedTab[], index: number): readonly [ClosedTab | undefined, readonly ClosedTab[]] {
  const tab = tabs[index];
  return [tab, tab === undefined ? tabs : tabs.filter((_, entry) => entry !== index)];
}

/** Reads this concern from the saved layout; an older or unreadable layout has no closed tabs. */
export function readRecentlyClosed(saved: string | null): readonly ClosedTab[] {
  if (saved === null) return [];
  try {
    const value: unknown = JSON.parse(saved);
    const closed: unknown = typeof value === "object" && value !== null && "recentlyClosed" in value ? value.recentlyClosed : null;
    return Array.isArray(closed) ? closed.filter(
      (entry: unknown): entry is ClosedTab => typeof entry === "object" && entry !== null
        && "route" in entry && typeof entry.route === "string" && isAppPath(entry.route)
        && "name" in entry && typeof entry.name === "string",
    ).slice(0, RECENTLY_CLOSED_LIMIT) : [];
  } catch {
    return [];
  }
}

/** Serializes beside the layout; the controller alone writes the result to storage. */
export function writeRecentlyClosed(layout: object, tabs: readonly ClosedTab[]): string {
  return JSON.stringify({ ...layout, recentlyClosed: tabs });
}
